(function (root, factory) {
  var engine = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
  }
  root.PaycheckEngine = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  // 2025 federal income tax brackets
  var FEDERAL_BRACKETS = {
    single: [
      { limit: 11925, rate: 0.10 },
      { limit: 48475, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250525, rate: 0.32 },
      { limit: 626350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    married: [
      { limit: 23850, rate: 0.10 },
      { limit: 96950, rate: 0.12 },
      { limit: 206700, rate: 0.22 },
      { limit: 394600, rate: 0.24 },
      { limit: 501050, rate: 0.32 },
      { limit: 751600, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    head: [
      { limit: 17000, rate: 0.10 },
      { limit: 64850, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250500, rate: 0.32 },
      { limit: 626350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ]
  };

  // 2025 standard deductions
  // Enacted 2025 amounts (post-July-2025 legislation), not the earlier
  // inflation-adjusted figures of $15,000/$30,000/$22,500.
  var STANDARD_DEDUCTIONS = {
    single: 15750,
    married: 31500,
    head: 23625
  };

  // 2025 Additional Medicare Tax (0.9%) thresholds. The threshold is
  // filing-status dependent: $250,000 for married filing jointly, $200,000
  // for single and head of household.
  var ADDITIONAL_MEDICARE_THRESHOLDS = {
    single: 200000,
    married: 250000,
    head: 200000
  };

  function getPeriodsPerYear(payFrequency) {
    var map = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 };
    return map[payFrequency] || 26;
  }

  function calculateProgressiveTax(income, brackets) {
    var tax = 0;
    var previousLimit = 0;

    for (var i = 0; i < brackets.length; i++) {
      var bracket = brackets[i];
      if (income > previousLimit) {
        var taxableInBracket = Math.min(income, bracket.limit) - previousLimit;
        tax += taxableInBracket * bracket.rate;
        previousLimit = bracket.limit;
      }
    }

    return Math.max(0, tax);
  }

  // Locates the bracket a given taxable income lands in, so callers can
  // describe the reader's actual marginal rate without re-deriving the table.
  function findMarginalBracket(income, brackets) {
    var lowerBound = 0;

    for (var i = 0; i < brackets.length; i++) {
      if (income <= brackets[i].limit) {
        return { rate: brackets[i].rate, lowerBound: lowerBound, upperBound: brackets[i].limit };
      }
      lowerBound = brackets[i].limit;
    }

    var last = brackets[brackets.length - 1];
    return { rate: last.rate, lowerBound: lowerBound, upperBound: last.limit };
  }

  function calculate(input) {
    var periodsPerYear = getPeriodsPerYear(input.payFrequency);
    var grossPayPerPeriod = input.salary / periodsPerYear;

    var contribution401kAnnual = Math.min(input.salary * (input.contribution401kPercent / 100), 23500);
    var contribution401kPerPeriod = contribution401kAnnual / periodsPerYear;

    var preTaxDeductionsPerPeriod = contribution401kPerPeriod + input.healthInsurance + input.hsa + input.otherPreTax;
    var taxableIncomePerPeriod = grossPayPerPeriod - preTaxDeductionsPerPeriod;
    var taxableIncomeAnnual = taxableIncomePerPeriod * periodsPerYear;

    var filingStatus = input.filingStatus || 'single';
    var brackets = FEDERAL_BRACKETS[filingStatus] || FEDERAL_BRACKETS.single;
    var standardDeduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single;
    var adjustedIncome = Math.max(0, taxableIncomeAnnual - standardDeduction);

    var federalTaxAnnual = calculateProgressiveTax(adjustedIncome, brackets);
    var federalTaxPerPeriod = federalTaxAnnual / periodsPerYear;
    var marginalBracket = findMarginalBracket(adjustedIncome, brackets);

    // State tax uses the same pre-tax-reduced wage base as the federal
    // calculation, floored at zero. Without the floor, pre-tax deductions
    // larger than gross pay produced a negative "tax" that inflated net pay.
    var stateTaxableIncomeAnnual = Math.max(0, taxableIncomeAnnual);
    var stateTaxAnnual = stateTaxableIncomeAnnual * ((input.stateRate || 0) / 100);
    var stateTaxPerPeriod = stateTaxAnnual / periodsPerYear;

    var socialSecurityAnnual = Math.min(input.salary, 176100) * 0.062;
    var socialSecurityPerPeriod = socialSecurityAnnual / periodsPerYear;

    var additionalMedicareThreshold =
      ADDITIONAL_MEDICARE_THRESHOLDS[filingStatus] || ADDITIONAL_MEDICARE_THRESHOLDS.single;
    var medicareAnnual = input.salary * 0.0145;
    if (input.salary > additionalMedicareThreshold) {
      medicareAnnual += (input.salary - additionalMedicareThreshold) * 0.009;
    }
    var medicarePerPeriod = medicareAnnual / periodsPerYear;

    var totalTaxesPerPeriod = federalTaxPerPeriod + stateTaxPerPeriod + socialSecurityPerPeriod + medicarePerPeriod;
    var totalDeductionsPerPeriod = preTaxDeductionsPerPeriod + totalTaxesPerPeriod;
    var netPayPerPeriod = grossPayPerPeriod - totalDeductionsPerPeriod;

    return {
      periodsPerYear: periodsPerYear,
      grossPayPerPeriod: grossPayPerPeriod,
      contribution401kAnnual: contribution401kAnnual,
      contribution401kPerPeriod: contribution401kPerPeriod,
      preTaxDeductionsPerPeriod: preTaxDeductionsPerPeriod,
      taxableIncomePerPeriod: taxableIncomePerPeriod,
      taxableIncomeAnnual: taxableIncomeAnnual,
      standardDeduction: standardDeduction,
      federalTaxableIncomeAnnual: adjustedIncome,
      federalMarginalRate: marginalBracket.rate,
      federalBracketLowerBound: marginalBracket.lowerBound,
      federalBracketUpperBound: marginalBracket.upperBound,
      federalTaxAnnual: federalTaxAnnual,
      federalTaxPerPeriod: federalTaxPerPeriod,
      stateTaxableIncomeAnnual: stateTaxableIncomeAnnual,
      additionalMedicareThreshold: additionalMedicareThreshold,
      stateTaxAnnual: stateTaxAnnual,
      stateTaxPerPeriod: stateTaxPerPeriod,
      socialSecurityAnnual: socialSecurityAnnual,
      socialSecurityPerPeriod: socialSecurityPerPeriod,
      medicareAnnual: medicareAnnual,
      medicarePerPeriod: medicarePerPeriod,
      totalTaxesPerPeriod: totalTaxesPerPeriod,
      totalDeductionsPerPeriod: totalDeductionsPerPeriod,
      netPayPerPeriod: netPayPerPeriod,
      annualNetPay: netPayPerPeriod * periodsPerYear
    };
  }

  return {
    calculate: calculate,
    getPeriodsPerYear: getPeriodsPerYear,
    calculateProgressiveTax: calculateProgressiveTax
  };
});
